import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import { MenuItem, TextField, Autocomplete } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useFormik } from "formik";
import * as yup from "yup";
import UserService from "../../services/user";
import KidService from "../../services/kids";
import CategoryService from "../../services/categories";
import CertificateHtml from "../certificate/certifcateHtml";
import { differenceInCalendarDays } from "date-fns";
import moment from "moment-timezone";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const validationSchema = yup.object({
  mentor_id: yup.string("Enter Mentor name").required("Mentor is required"),
  category_id: yup.string("Enter Category").required("Category is required"),
  kid_id: yup.string("Enter Kid name").required("Kid is required"),
  amount: yup.string("Enter Amount").required("Amount is required"),
  email: yup.string("Enter email").email().required("Email is required"),
});

export default function SponsoredModal({
  open,
  onClose,
  onSubmit,
  selectedRow,
}) {
  const [rowMentor, setRowMentor] = useState([]);
  const [rowKids, setRowKids] = useState([]);
  const [rowCategory, setRowCategory] = useState([]);
  const childRef = useRef(null);
  const [certifcateData, setCertifcateData] = useState(null);

  function generateRandomString(length) {
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < length; i++) {
      result += characters.charAt(
        Math.floor(Math.random() * characters.length),
      );
    }
    return result;
  }

  const formik = useFormik({
    initialValues: {
      mentor_id: selectedRow.mentor_id || "",
      email: selectedRow.email || "",
      for_months: selectedRow.for_months || "",
      amount: selectedRow.amount || "",
      kid_id: selectedRow.kid_id || "",
      category_id: selectedRow.category_id || "",
      mentored_on_date:
        selectedRow.mentored_on_date || moment().format("YYYY-MM-DD"),
      mentoring_left_date:
        selectedRow.mentoring_left_date || moment().format("YYYY-MM-DD"),
    },
    validationSchema: validationSchema,
    onSubmit: (values, { setFieldError }) => {
      if (selectedRow.id) {
        values.id = selectedRow.id;
      }

      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const monthsToAdd = parseInt(values.for_months, 10);
      if (isNaN(monthsToAdd) || monthsToAdd < 1) {
        setFieldError("for_months", "Please enter a valid number of months.");
        return;
      }

      const mentoredOn = moment
        .tz(values.mentored_on_date, "YYYY-MM-DD", userTimeZone)
        .startOf("day");
      const mentoringLeft = moment
        .tz(values.mentoring_left_date, "YYYY-MM-DD", userTimeZone)
        .startOf("day");

      if (!mentoredOn.isValid() || !mentoringLeft.isValid()) {
        setFieldError("mentoring_left_date", "Invalid dates provided.");
        return;
      }

      const expectedEnd = mentoredOn.clone().add(monthsToAdd, "months");

      const actualDays =
        differenceInCalendarDays(mentoringLeft.toDate(), mentoredOn.toDate()) +
        1;
      const expectedDays =
        differenceInCalendarDays(expectedEnd.toDate(), mentoredOn.toDate()) + 1;

      if (actualDays !== expectedDays) {
        setFieldError(
          "mentoring_left_date",
          `Date range should be exactly ${expectedDays} days (for ${monthsToAdd} month(s)), but it is ${actualDays} day(s).`,
        );
        return;
      }

      values.mentored_on_date = mentoredOn.format("DD/MM/YYYY");
      values.mentoring_left_date = mentoringLeft.format("DD/MM/YYYY");

      const mentorData = rowMentor.find((m) => m.id === values.mentor_id);

      console.log(values);
      const certifcateData = {
        receipt_no: generateRandomString(10),
        mentor_name: mentorData.full_name,
        donation_by: "Cash",
        address: mentorData.address,
        pan_number: mentorData.pan_no,
        transaction_id: "N/A",
        amount: values.amount,
        payment_date: values.mentored_on_date,
        kid_id: values.kid_id,
        email: values.email,
      };

      setCertifcateData(certifcateData);
      childRef.current.htmlToImageConvert();
      onSubmit(values);
      formik.resetForm();
    },
  });

  const getMentors = async () => {
    try {
      let { data } = await UserService.fetchMentor();
      setRowMentor(data.data);
    } catch (err) {
      console.log(err);
    }
  };
  const getKids = async () => {
    try {
      let { data } = await KidService.fetchKids();
      setRowKids((data.data || []).filter((k) => k.is_sponsored === false));
    } catch (err) {
      console.log(err);
    }
  };

  const getCategories = async () => {
    try {
      let { data } = await CategoryService.fetchCategories();
      setRowCategory(data.data);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    getMentors();
    getKids();
    getCategories();
  }, []);

  useEffect(() => {
    if (formik.values.mentor_id) {
      const mentorEmail = rowMentor.find(
        (m) => m.id === formik.values.mentor_id,
      )?.email;
      formik.setFieldValue("email", mentorEmail);
    }
  }, [formik.values.mentor_id]);

  const monthsOptions = [
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "10",
    "11",
    "12",
  ];
  return (
    <div>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style} className="modal-lg sponsored-modal">
          <Typography id="modal-modal-title" variant="h6" component="h2">
            {selectedRow.id ? "Update" : "Add"} Sponsored
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12}>
              <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                <form onSubmit={formik.handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        fullWidth
                        size="small"
                        options={rowMentor || []}
                        getOptionLabel={(option) =>
                          option?.full_name
                            ? `${option.full_name}${option.email ? ` (${option.email})` : ""}`
                            : ""
                        }
                        value={
                          rowMentor?.find(
                            (m) =>
                              String(m.id) === String(formik.values.mentor_id),
                          ) || null
                        }
                        onChange={(_, newValue) => {
                          formik.setFieldValue("mentor_id", newValue?.id ?? "");
                          if (newValue?.email) {
                            formik.setFieldValue("email", newValue.email);
                          }
                        }}
                        onBlur={() => formik.setFieldTouched("mentor_id", true)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Mentor"
                            placeholder="Search mentor by name or email"
                            margin="dense"
                            error={
                              formik.touched.mentor_id &&
                              Boolean(formik.errors.mentor_id)
                            }
                            helperText={
                              formik.touched.mentor_id &&
                              formik.errors.mentor_id
                            }
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        id="email"
                        name="email"
                        label="Email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        multiline
                        margin="dense"
                        size="small"
                        error={
                          formik.touched.email && Boolean(formik.errors.email)
                        }
                        helperText={formik.touched.email && formik.errors.email}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        fullWidth
                        size="small"
                        options={rowKids?.filter((k) => !k.is_sponsored) || []}
                        getOptionLabel={(option) =>
                          option?.name
                            ? `${option.name}${option.enrollment_id ? ` (${option.enrollment_id})` : ""}`
                            : ""
                        }
                        value={
                          rowKids?.find(
                            (k) =>
                              String(k.id) === String(formik.values.kid_id),
                          ) || null
                        }
                        onChange={(_, newValue) => {
                          formik.setFieldValue("kid_id", newValue?.id ?? "");
                        }}
                        onBlur={() => formik.setFieldTouched("kid_id", true)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Kid"
                            placeholder="Search kid by name or enrollment"
                            margin="dense"
                            error={
                              formik.touched.kid_id &&
                              Boolean(formik.errors.kid_id)
                            }
                            helperText={
                              formik.touched.kid_id && formik.errors.kid_id
                            }
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        id="amount"
                        name="amount"
                        label="Amount"
                        value={formik.values.amount}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        multiline
                        margin="dense"
                        size="small"
                        type="number"
                        error={
                          formik.touched.amount && Boolean(formik.errors.amount)
                        }
                        helperText={
                          formik.touched.amount && formik.errors.amount
                        }
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        select
                        id="category_id"
                        name="category_id"
                        label="Select Category"
                        value={formik.values.category_id}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        multiline
                        margin="dense"
                        size="small"
                        type="number"
                        error={
                          formik.touched.category_id &&
                          Boolean(formik.errors.category_id)
                        }
                        helperText={
                          formik.touched.category_id &&
                          formik.errors.category_id
                        }
                      >
                        <MenuItem value={""}>Select Mentor</MenuItem>
                        {rowCategory?.map((c) => (
                          <MenuItem value={c.id}>{c.category_name}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        name="for_months"
                        label="For Months"
                        defaultValue=""
                        value={formik.values.for_months}
                        margin="dense"
                        size="small"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        fullWidth
                      >
                        <MenuItem value={""}>Select Months</MenuItem>
                        {monthsOptions?.map((m) => (
                          <MenuItem value={m}>{m}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="mentored_on_date"
                        label="Mentoring date"
                        type="date"
                        defaultValue=""
                        value={formik.values.mentored_on_date}
                        margin="dense"
                        size="small"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        fullWidth
                      ></TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="mentoring_left_date"
                        label="Mentoring left date"
                        type="date"
                        value={formik.values.mentoring_left_date}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.mentoring_left_date &&
                          Boolean(formik.errors.mentoring_left_date)
                        }
                        helperText={
                          formik.touched.mentoring_left_date &&
                          formik.errors.mentoring_left_date
                        }
                        margin="dense"
                        size="small"
                        fullWidth
                      />
                    </Grid>
                  </Grid>

                  <div className="text-right">
                    <Button
                      color="primary"
                      sx={{ mr: 1, mt: 1 }}
                      variant="contained"
                      type="submit"
                    >
                      {selectedRow.id ? "Update" : "Add"}
                    </Button>
                    <Button onClick={onClose} sx={{ mt: 1 }} variant="outlined">
                      Cancel
                    </Button>
                  </div>
                </form>
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12}>
            <Box className="hide-certificate" style={{ width: 600 }}>
              <Typography variant="h6" component="h2">
                Certificate Preview
              </Typography>
              <CertificateHtml ref={childRef} data={certifcateData} />
            </Box>
          </Grid>
        </Box>
      </Modal>
    </div>
  );
}
